/**
 * One withdrawal, in full.
 *
 * The list answers "what needs attention"; this answers "what happened to this
 * one" — the status timeline, the gateway's own reference, and the ledger
 * entries the request produced. That last part matters: when a pharmacy asks
 * where its money went, the answer is two postings, and they are here rather
 * than in a database someone has to be talked through.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { MdContentCopy, MdSync } from "react-icons/md";
import API from "Config/API";
import { toast } from "react-toastify";

import { StatusChip } from "components/ui";
import { T, tint } from "theme/tokens";
import { money } from "./PayoutActionDialogs";

const when = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
};

function Field({ label, value, mono }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: T.muted,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.82rem",
          color: T.ink,
          fontFamily: mono ? "monospace" : undefined,
          wordBreak: "break-all",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function PayoutDetailDialog({ open, requestId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await API.get(`/admin/payouts/${requestId}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load the request.");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await API.post(`/admin/payouts/${requestId}/sync`);
      toast.info(res.data?.message || "Synced.");
      await load();
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not reach Razorpay.");
    } finally {
      setSyncing(false);
    }
  };

  const r = data?.request;
  const copy = (v) => {
    if (!v) return;
    navigator.clipboard?.writeText(String(v));
    toast.info("Copied");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: "1rem" }}>
              {loading ? <Skeleton width={180} /> : r?.pharmacyName || r?.pharmacyId}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: T.muted, fontFamily: "monospace" }}>
              {requestId}
            </Typography>
          </Box>
          {r && <StatusChip status={r.status} />}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack spacing={1.5}>
            <Skeleton height={64} />
            <Skeleton height={110} />
          </Stack>
        ) : !r ? (
          <Typography sx={{ color: T.muted }}>Nothing to show.</Typography>
        ) : (
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: tint(T.success, 0.08),
                border: `1px solid ${tint(T.success, 0.25)}`,
              }}
            >
              <Typography
                sx={{ fontSize: "1.7rem", fontWeight: 800, color: T.success, fontVariantNumeric: "tabular-nums" }}
              >
                {money(r.amount)}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: T.muted }}>
                {r.bankDetails?.bankName || "Bank"} · {r.bankDetails?.accountNumber || "—"} ·{" "}
                {r.bankDetails?.ifscCode || "—"}
              </Typography>
            </Box>

            <Stack direction="row" flexWrap="wrap" useFlexGap gap={2.5}>
              <Field label="Requested" value={when(r.requestedAt || r.createdAt)} />
              <Field label="Decided" value={when(r.decidedAt)} />
              <Field label="Paid" value={when(r.paidAt)} />
              <Field label="Mode" value={r.payoutMode} />
              <Field label="Razorpay payout" value={r.razorpay?.payoutId} mono />
              <Field label="UTR" value={r.razorpay?.utr || r.manualReference} mono />
            </Stack>

            {(r.rejectionReason || r.razorpay?.failureReason) && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: tint(T.error, 0.08),
                  border: `1px solid ${tint(T.error, 0.25)}`,
                }}
              >
                <Typography sx={{ fontSize: "0.82rem", color: T.error }}>
                  {r.rejectionReason || r.razorpay?.failureReason}
                </Typography>
              </Box>
            )}

            {r.statusHistory?.length > 0 && (
              <Box>
                <Typography
                  sx={{ fontSize: "0.62rem", fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.8 }}
                >
                  Timeline
                </Typography>
                <Stack spacing={0.9}>
                  {r.statusHistory.map((h, i) => (
                    <Stack key={i} direction="row" spacing={1.2} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 8, height: 8, borderRadius: "50%", mt: 0.7, flexShrink: 0,
                          bgcolor: i === r.statusHistory.length - 1 ? T.primary : T.border,
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: "0.8rem", color: T.ink, textTransform: "capitalize" }}>
                          {h.status}
                          <Box component="span" sx={{ color: T.subtle, ml: 0.8, fontSize: "0.72rem" }}>
                            {when(h.at)} · {h.by || "system"}
                          </Box>
                        </Typography>
                        {h.note && (
                          <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>{h.note}</Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {data?.ledger?.length > 0 && (
              <Box>
                <Typography
                  sx={{ fontSize: "0.62rem", fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.8 }}
                >
                  Ledger
                </Typography>
                <Stack spacing={0.6}>
                  {data.ledger.map((p, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: "0.74rem", color: T.muted, fontFamily: "monospace" }}>
                        {p.accountKey}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          color: p.direction === "credit" ? T.success : T.error,
                        }}
                      >
                        {p.direction === "credit" ? "+" : "−"}
                        {money(p.amount)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Tooltip title="Copy request ID">
          <IconButton size="small" onClick={() => copy(requestId)}>
            <MdContentCopy size={16} />
          </IconButton>
        </Tooltip>
        {r?.razorpay?.payoutId && r.status !== "paid" && (
          <Button size="small" startIcon={<MdSync />} onClick={sync} disabled={syncing} sx={{ color: T.primary }}>
            Re-check with Razorpay
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: T.muted }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
